import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, addDays, getDay } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { Link } from "react-router-dom";
import { api, errMsg, TIPOS_CITA, HUECOS_TIPO, textOn, waLink } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { PageTitle } from "@/components/Badges";
import { ApptDialog } from "@/components/ApptDialog";
import { RecordatoriosManana } from "@/components/RecordatoriosManana";
import { SemanaSab } from "@/components/SemanaSab";
import { HuecoDiaDialog } from "@/components/HuecoDiaDialog";
import { MesLateral } from "@/components/MesLateral";
import { VistasRapidas } from "@/components/VistasRapidas";
import { WaTodas } from "@/components/WaTodas";
import { avisarCambio, msgCitas } from "@/lib/avisoProf";
import { Printer, MessageCircle, CheckCheck } from "lucide-react";

const EventoCita = ({ event: e }) => (
  <span data-testid={e.hueco ? undefined : `agenda-cita-${e.id}`}>
    {!e.hueco && e.recordado_at && <MessageCircle className="mr-0.5 inline h-3 w-3 align-[-2px]" strokeWidth={2.5} aria-label="Recordatorio enviado" data-testid={`agenda-recordado-${e.id}`} />}
    {!e.hueco && e.estado === "confirmada" && <CheckCheck className="mr-0.5 inline h-3 w-3 align-[-2px]" strokeWidth={2.5} aria-label="Confirmada" data-testid={`agenda-confirmada-${e.id}`} />}
    {e.title}
  </span>
);

const VIEWS = { day: true, work_week: SemanaSab };

const localizer = dateFnsLocalizer({ format, parse, startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 1 }), getDay, locales: { es } });
const DnDCalendar = withDragAndDrop(Calendar);
const ymd = (d) => format(d, "yyyy-MM-dd");
const hm = (d) => format(d, "HH:mm");
const MIN = new Date(1970, 0, 1, 10, 0);
const MAX = new Date(1970, 0, 1, 20, 15);
const MESSAGES = { today: "Hoy", previous: "‹", next: "›", day: "Día", week: "Semana", work_week: "Semana", noEventsInRange: "Sin citas", showMore: (n) => `+${n}` };

const toEvent = (a) => {
  const anulada = a.estado === "anulada";
  return {
    ...a,
    allDay: anulada,
    start: new Date(`${a.fecha}T${anulada ? "00:00" : a.inicio}`),
    end: new Date(`${a.fecha}T${anulada ? "00:00" : a.fin}`),
    resourceId: a.profesional_id,
    title: [anulada ? `ANULADA ${a.inicio}` : "", a.bono_quedan != null && a.bono_quedan <= 2 ? "RENOVAR BONO" : "", a.tipo !== "otro" && a.tipo !== "bloqueo" ? TIPOS_CITA[a.tipo]?.label : "", a.patient ? `${a.patient.nombre}${a.patient.codigo ? ` (nº ${a.patient.codigo})` : ""}` : "", a.titulo, a.tto_info].filter(Boolean).join(" · ") || TIPOS_CITA[a.tipo]?.label,
  };
};

const toHueco = (h) => ({
  ...h, hueco: true, raw: h, id: `h-${h.id}-${h.fecha}`,
  start: new Date(`${h.fecha}T${h.inicio}`),
  end: new Date(`${h.fecha}T${h.fin}`),
  resourceId: h.profesional_id,
  title: [HUECOS_TIPO[h.tipo]?.label, h.nota].filter(Boolean).join(" · "),
});

const ProfHeader = ({ p, libre, onToggle, onWa }) => (
  <div className="rounded px-2 py-1" style={{ background: p.color, color: textOn(p.color) }}>
    <div>{p.etiqueta || p.nombre}</div>
    <div className="flex flex-wrap justify-center gap-1">
      <button type="button" onClick={() => onToggle(p)} data-testid={`agenda-libre-${p.id}`}
        className="mt-0.5 rounded bg-white/70 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-white">
        {libre ? "No trabaja hoy · deshacer" : "Quitar sus huecos hoy"}
      </button>
      <button type="button" onClick={() => onWa(p)} data-testid={`agenda-wa-prof-${p.id}`} title="Enviarle por WhatsApp sus citas del día"
        className="mt-0.5 rounded bg-white/70 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-white">WhatsApp</button>
      <a href={`/agenda/imprimir-semana?fecha=${p._fecha}&prof=${p.id}`} target="_blank" rel="noreferrer" data-testid={`agenda-semana-prof-${p.id}`}
        className="mt-0.5 rounded bg-white/70 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-white">Semana</a>
    </div>
  </div>
);

export default function Agenda() {
  const { centro } = useCentro();
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState("day");
  const [profs, setProfs] = useState([]);
  const [appts, setAppts] = useState([]);
  const [huecos, setHuecos] = useState([]);
  const [sel, setSel] = useState(null);
  const [libres, setLibres] = useState([]);
  const [hdia, setHdia] = useState(null);
  const [verMes, setVerMes] = useState(() => localStorage.getItem("agenda_ver_mes") !== "0");
  const toggleMes = () => setVerMes((v) => { localStorage.setItem("agenda_ver_mes", v ? "0" : "1"); return !v; });
  const [ocultos, setOcultos] = useState(() => JSON.parse(localStorage.getItem("agenda_ocultos") || "[]"));
  const toggleProf = (id) => setOcultos((o) => { const n = o.includes(id) ? o.filter((x) => x !== id) : [...o, id]; localStorage.setItem("agenda_ocultos", JSON.stringify(n)); return n; });
  const setOcultosLS = (n) => { localStorage.setItem("agenda_ocultos", JSON.stringify(n)); setOcultos(n); };
  const visibles = useMemo(() => profs.filter((p) => !ocultos.includes(p.id)), [profs, ocultos]);

  const range = useMemo(() => (view === "day" ? [date, date] : [startOfWeek(date, { weekStartsOn: 1 }), addDays(startOfWeek(date, { weekStartsOn: 1 }), 5)]), [date, view]);
  const load = useCallback(() => {
    const params = { desde: ymd(range[0]), hasta: ymd(range[1]), centro };
    api.get("/appointments", { params }).then((r) => setAppts(r.data));
    api.get("/agenda-huecos", { params }).then((r) => setHuecos(r.data));
    api.get("/dias-libres", { params }).then((r) => setLibres(r.data));
  }, [range, centro]);
  useEffect(() => {
    api.get("/profesionales", { params: { centro } }).then((r) => setProfs(r.data.map((p) => (p.nombre.trim().toLowerCase().startsWith("laura") && !p.nombre.includes("(") && p.centro === "MADRID" ? { ...p, etiqueta: `${p.nombre.trim()} (Madrid)` } : p))));
  }, [centro]);
  useEffect(() => {
    load();
  }, [load]);

  const events = useMemo(() => [...appts.map(toEvent), ...huecos.map(toHueco)].filter((e) => !ocultos.includes(e.profesional_id)), [appts, huecos, ocultos]);
  const dragRef = useRef(null);
  useEffect(() => {
    const clear = () => setTimeout(() => { dragRef.current = null; }, 50);
    window.addEventListener("mouseup", clear);
    return () => window.removeEventListener("mouseup", clear);
  }, []);
  const profColor = useMemo(() => Object.fromEntries(profs.map((p) => [p.id, p.color])), [profs]);
  const fromHueco = async (h) => {
    const base = { fecha: h.fecha, inicio: h.inicio, fin: h.fin, profesional_id: h.profesional_id, tipo: HUECOS_TIPO[h.tipo].cita, titulo: h.tipo === "comida" || h.tipo === "no_agendar" ? HUECOS_TIPO[h.tipo].label.toUpperCase() : "", hueco: h };
    if (h.tipo !== "terapia" || !h.nota) return setSel(base);
    const { data } = await api.get("/patients/por-nombre", { params: { nombre: h.nota } }).catch(() => ({ data: [] }));
    if (data.length === 1) return setSel({ ...base, patient: data[0], patient_id: data[0].id });
    if (data.length > 1) toast.info(`Hay ${data.length} pacientes que se llaman «${h.nota}». Elige el correcto en la cita.`);
    else toast.info(`No encuentro a «${h.nota}» en las fichas. Búscalo a mano o créalo.`);
    setSel({ ...base, notas: h.nota });
  };
  const moverCita = async (a, body) => {
    let siguientes = false;
    if (a.serie_id) {
      const r = window.prompt("Esta cita se repite. Escribe 1 para mover solo esta, o 2 para mover esta y todas las siguientes de la serie (mismo cambio de día y hora).", "1");
      if (r !== "1" && r !== "2") return;
      siguientes = r === "2";
    }
    const { data } = await api.post(`/appointments/${a.id}/mover`, { ...body, siguientes });
    if (data.conflictos?.length) toast.warning(`${data.movidas} citas movidas. No se han movido ${data.conflictos.length} porque el hueco ya está ocupado: ${data.conflictos.map((x) => x.split("-").reverse().join("/")).join(", ")}`, { duration: 12000 });
    else toast.success(data.movidas > 1 ? `${data.movidas} citas movidas` : `Cita movida a ${format(new Date(`${body.fecha}T${body.inicio}`), "EEEE d", { locale: es })} ${body.inicio}`);
    if (data.movidas) avisarCambio({ profs, orig: a, nuevo: body, accion: "cambio" });
  };
  const move = async ({ event, start, end, resourceId }) => {
    const body = { fecha: ymd(start), inicio: hm(start), fin: hm(end), profesional_id: resourceId || event.profesional_id };
    try {
      if (event.hueco) {
        const { data } = await api.post("/huecos-dia/mover", { ...body, hueco_id: event.raw.id, fecha_origen: event.raw.fecha, tipo: event.raw.tipo, nota: event.raw.nota || "" });
        toast.success(body.fecha === event.raw.fecha ? `Hueco cambiado solo el ${format(start, "EEEE d", { locale: es })}` : `Hueco pasado al ${format(start, "EEEE d", { locale: es })}`);
        if (data.aviso) toast.warning(data.aviso, { duration: 8000 });
      } else await moverCita(event, body);
    } catch (e) {
      toast.error(errMsg(e));
    }
    load();
  };
  const dropDia = (fecha) => {
    const ev = dragRef.current;
    dragRef.current = null;
    if (!ev || ev.fecha === fecha) return;
    move({ event: ev, start: new Date(`${fecha}T${ev.inicio}`), end: new Date(`${fecha}T${ev.fin}`), resourceId: ev.profesional_id });
  };
  const toggleLibre = async (p) => {
    try {
      const { data } = await api.post("/dias-libres", { profesional_id: p.id, fecha: ymd(date) });
      toast.success(data.libre ? `Quitados los huecos de ${p.nombre} el ${format(date, "EEEE d", { locale: es })}` : `Huecos de ${p.nombre} recuperados`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    load();
  };
  const esLibre = (pid) => libres.some((l) => l.profesional_id === pid && l.fecha === ymd(date));
  const waProf = async (p) => {
    let tel = p.telefono;
    if (!tel) {
      tel = window.prompt(`Teléfono móvil de ${p.nombre} (se guarda para la próxima vez):`, "");
      if (!tel?.trim()) return;
      try {
        await api.post(`/profesionales/${p.id}/telefono`, { telefono: tel });
        const pr = profs.find((x) => x.id === p.id);
        if (pr) pr.telefono = tel;
      } catch (e) {
        return toast.error(errMsg(e));
      }
    }
    window.open(waLink(tel, msgProf(p)), "_blank");
  };
  const citasProf = (p) => appts.filter((a) => a.profesional_id === p.id && a.fecha === ymd(date) && a.estado !== "anulada" && a.tipo !== "bloqueo").sort((x, y) => x.inicio.localeCompare(y.inicio));
  const msgProf = (p) => msgCitas(p, citasProf(p), date);
  const eventStyle = (ev) => {
    if (ev.hueco) {
      const c = HUECOS_TIPO[ev.tipo]?.color || "#64748B";
      return { className: "agenda-hueco", style: { background: `${c}1F`, color: c, border: `1px dashed ${c}`, fontSize: 10, fontWeight: 600, borderRadius: 4 } };
    }
    const c = ev.tipo === "bloqueo" ? "#E2E8F0" : profColor[ev.profesional_id] || TIPOS_CITA[ev.tipo]?.color || "#64748B";
    const off = ev.estado === "anulada" || ev.estado === "no_vino";
    const renovar = ev.bono_quedan != null && ev.bono_quedan <= 2;
    return { style: { background: c, color: ev.tipo === "bloqueo" ? "#334155" : textOn(c), border: `1px solid rgba(0,0,0,.15)`, borderLeft: `4px solid ${TIPOS_CITA[ev.tipo]?.color || c}`, boxShadow: renovar ? "inset 0 0 0 2px #EA580C" : "none", opacity: off ? 0.45 : 1, textDecoration: off ? "line-through" : "none", fontSize: 11, borderRadius: 4 } };
  };

  return (
    <div data-testid="agenda-page">
      <PageTitle title="Agenda" subtitle="Pulsa un hueco para dar cita. Arrastra citas y huecos para moverlos (también a otro día del calendario mensual) o estira el borde inferior para cambiar la duración." />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-[11px]">
        <span className="font-semibold text-slate-500">Borde de la cita:</span>
        <span className="flex items-center gap-1" data-testid="legend-renovar"><span className="h-3 w-3 rounded-sm" style={{ boxShadow: "inset 0 0 0 2px #EA580C" }} />Renovar bono (quedan 2 o menos)</span>
        <span className="flex items-center gap-1" data-testid="legend-recordado"><MessageCircle className="h-3 w-3 text-[#25D366]" strokeWidth={2.5} />Recordatorio enviado</span>
        <span className="flex items-center gap-1" data-testid="legend-confirmada"><CheckCheck className="h-3 w-3 text-emerald-600" strokeWidth={2.5} />Confirmada</span>
        {Object.entries(TIPOS_CITA).map(([k, t]) => <span key={k} className="inline-flex items-center gap-1"><span className="h-2.5 w-1 rounded-sm" style={{ background: t.color }} />{t.label}</span>)}
        <span className="font-semibold text-slate-500 ml-2">Huecos:</span>
        {Object.entries(HUECOS_TIPO).map(([k, t]) => <span key={k} className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm border border-dashed" style={{ borderColor: t.color, background: `${t.color}1F` }} />{t.label}</span>)}
        <button type="button" onClick={toggleMes} data-testid="agenda-toggle-mes" className="ml-auto rounded-md border border-slate-300 px-2.5 py-1 font-medium text-slate-700 hover:bg-slate-50">{verMes ? "Ocultar calendario del mes" : "Ver calendario del mes"}</button>
        <Link to="/planillas" data-testid="agenda-semana-tipo-link" className="rounded-md border border-slate-300 px-2.5 py-1 font-medium text-slate-700 hover:bg-slate-50">Editar semana tipo</Link>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-3 text-[11px]" data-testid="agenda-quien">
        <span className="font-semibold text-slate-500 mr-1">Ver:</span>
        {profs.map((p) => {
          const on = !ocultos.includes(p.id);
          return (
            <button key={p.id} type="button" onClick={() => toggleProf(p.id)} data-testid={`agenda-ver-${p.id}`}
              className={`rounded-full border px-2.5 py-0.5 font-medium transition-opacity ${on ? "" : "opacity-40 line-through"}`}
              style={on ? { background: p.color, borderColor: p.color, color: textOn(p.color) } : { borderColor: "#CBD5E1", color: "#64748B" }}>{p.etiqueta || p.nombre}</button>
          );
        })}
        {ocultos.length > 0 && <button type="button" onClick={() => setOcultosLS([])} data-testid="agenda-ver-todos" className="ml-1 text-[#1E3A8A] underline">Ver todos</button>}
        <VistasRapidas profs={profs} ocultos={ocultos} setOcultos={setOcultosLS} />
        <a href={`/agenda/imprimir?fecha=${ymd(date)}&centro=${centro}&ids=${visibles.map((p) => p.id).join(",")}`} target="_blank" rel="noreferrer" data-testid="agenda-imprimir-dia"
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 font-medium text-slate-700 hover:bg-slate-50"><Printer className="h-3.5 w-3.5" />Imprimir el día</a>
        <a href={`/agenda/imprimir-semana?fecha=${ymd(date)}&prof=${visibles[0]?.id || ""}`} target="_blank" rel="noreferrer" data-testid="agenda-imprimir-semana"
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 font-medium text-slate-700 hover:bg-slate-50"><Printer className="h-3.5 w-3.5" />Imprimir la semana</a>
        <WaTodas profs={visibles} contar={(p) => citasProf(p).length} mensaje={msgProf} fechaTxt={format(date, "EEEE d 'de' MMMM", { locale: es })} />
      </div>
      <RecordatoriosManana profs={profs} reloadKey={appts} onChange={load} />
      <div className={`grid gap-3 ${verMes ? "lg:grid-cols-[minmax(0,1fr)_200px]" : ""}`}>
      <div className="bg-white border border-slate-200 rounded-lg p-3 agenda-wrap min-w-0" data-testid="agenda-calendar">
        <DnDCalendar
          localizer={localizer} culture="es" messages={MESSAGES}
          events={events} date={date} onNavigate={setDate} view={view} onView={setView} views={VIEWS}
          onDragStart={({ event }) => { dragRef.current = event; }}
          resources={view === "day" ? visibles : undefined} resourceIdAccessor="id" resourceTitleAccessor="nombre"
          components={{ event: EventoCita, resourceHeader: ({ resource }) => <ProfHeader p={{ ...resource, _fecha: ymd(date) }} libre={esLibre(resource.id)} onToggle={toggleLibre} onWa={waProf} /> }}
          min={MIN} max={MAX} step={15} timeslots={1} style={{ height: 1700 }}
          selectable resizable draggableAccessor={(ev) => !ev.allDay} resizableAccessor={(ev) => !ev.allDay} onEventDrop={move} onEventResize={move} eventPropGetter={eventStyle}
          onSelectSlot={({ start, end, resourceId }) => {
            const pid = resourceId || profs[0]?.id;
            const h = huecos.find((x) => x.fecha === ymd(start) && (view !== "day" || x.profesional_id === pid) && x.inicio <= hm(start) && hm(start) < x.fin);
            if (h && end - start <= 15 * 60000) return fromHueco(h);
            const e = end - start <= 15 * 60000 ? new Date(start.getTime() + 45 * 60000) : end;
            const fin = hm(e) > "20:15" || ymd(e) !== ymd(start) ? "20:15" : hm(e);
            setSel({ fecha: ymd(start), inicio: hm(start), fin: fin <= hm(start) ? "20:15" : fin, profesional_id: resourceId || profs[0]?.id });
          }}
          onSelectEvent={(ev) => (ev.hueco ? fromHueco(ev.raw) : setSel(appts.find((a) => a.id === ev.id)))}
        />
      </div>
      {verMes && <MesLateral date={date} centro={centro} profs={visibles} reloadKey={appts} onPick={setDate} onDrop={dropDia} />}
      </div>
      <ApptDialog appt={sel} profs={profs} onClose={() => { setSel(null); load(); }} onSaved={() => { setSel(null); load(); }} onEditHueco={(h) => { setSel(null); setHdia(h); }} />
      <HuecoDiaDialog h={hdia} profs={profs} onClose={() => setHdia(null)} onDone={() => { setHdia(null); load(); }} />
    </div>
  );
}
